import { useCallback, useState } from 'react';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import DialogContent from '@mui/material/DialogContent';
import { useStringsContext } from '../../../../contexts/strings';
import DialogActions from '@mui/material/DialogActions';
import { Alert, Box, Stack } from '@mui/material';
import BaseDialog, { BaseDialogProps } from '../../../common/base-dialog';
import { useSnackbar } from 'notistack';
import { useArticles } from '../hooks/useArticles';
import { Article, UpdateArticleInput } from '../types';
import { getArticleStatus } from '../utils/status';
import moment from 'moment';
import { SxStyle } from '../../../../types';

const styles: SxStyle = {
  preview: {
    maxWidth: 360,
    margin: 'auto',
    blockquote: {
      borderTop: '1px solid #ccc',
      borderBottom: '1px solid #ccc',
      margin: 0,
      padding: '1rem',
    },
  },
};

interface Props extends Pick<BaseDialogProps, 'open' | 'onClose'> {
  onDone: () => void;
  onEdit: () => void;
  article: Article;
  isReadOnly?: boolean;
}

function PreviewArticleModal({ open, onClose, onDone, onEdit, article, isReadOnly }: Props) {
  const { strings } = useStringsContext();
  const { enqueueSnackbar } = useSnackbar();
  const { updateArticle } = useArticles();
  const [submitLoading, setSubmitLoading] = useState(false);

  const onUpdateArticle = useCallback(
    async (articleData: UpdateArticleInput) => {
      setSubmitLoading(true);
      try {
        await updateArticle(articleData);

        onDone();
        onClose?.();
      } catch (error) {
        console.error(error);
        enqueueSnackbar(strings.messages.saveError, {
          variant: 'error',
        });
      }
      setSubmitLoading(false);
    },
    [strings, enqueueSnackbar, onDone, onClose, updateArticle]
  );

  const onPublish = () => {
    onUpdateArticle({ id: article.id, publishedAt: new Date(), archivedAt: null });
  };

  const onArchive = () => {
    onUpdateArticle({ id: article.id, archivedAt: new Date() });
  };

  const status = getArticleStatus(article);

  return (
    <BaseDialog open={open} onClose={onClose} title={strings.articles.previewArticle}>
      <DialogContent dividers>
        {status === 'scheduled' && (
          <Alert severity="info">
            {strings.articles.scheduledToPublishAt}{' '}
            {moment(article.publishedAt).format('YYYY-MM-DD HH:mm')}
          </Alert>
        )}
        <Box sx={styles.preview}>
          {article.imageUrl && (
            <img src={article.imageUrl} alt={article.title} style={{ width: '100%' }} />
          )}
          <h1>{article.title}</h1>
          <p>
            <b>{article.excerpt}</b>
          </p>
          <p dangerouslySetInnerHTML={{ __html: article.content }}></p>
        </Box>
      </DialogContent>
      <DialogActions>
        <Stack direction="row" gap={2} sx={{ justifyContent: 'space-between', flex: 1 }}>
          <Stack direction="row" gap={2}>
            <Button onClick={onEdit} disabled={isReadOnly}>
              {strings.articles.editArticle}
            </Button>
          </Stack>
          <Stack direction="row" gap={2}>
            <Button color="primary" onClick={onClose}>
              {strings.actions.cancel}
            </Button>
            {status === 'published' && (
              <LoadingButton
                variant="contained"
                color="error"
                onClick={onArchive}
                loading={submitLoading}
                disabled={isReadOnly}
              >
                {strings.actions.archive}
              </LoadingButton>
            )}
            {status !== 'published' && (
              <LoadingButton
                variant="contained"
                color="primary"
                onClick={onPublish}
                loading={submitLoading}
                disabled={isReadOnly}
              >
                {strings.actions.publishNow}
              </LoadingButton>
            )}
          </Stack>
        </Stack>
      </DialogActions>
    </BaseDialog>
  );
}

export default PreviewArticleModal;
